import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
const HomePage = lazy(() => import('./components/HomePage'));
const ReportForm = lazy(() => import('./components/ReportForm'));
const ReportsList = lazy(() => import('./components/ReportsList'));
const ReportDetail = lazy(() => import('./components/ReportDetail'));
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const SupportTickets = lazy(() => import('./components/SupportTickets'));
const ModeratorPanel = lazy(() => import('./components/ModeratorPanel'));
const ModeratorDashboard = lazy(() => import('./components/ModeratorDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
import { Toaster } from './components/ui/sonner';
import './App.css';
import AboutUs from "./components/AboutUs";  
import MottoPolicy from "./components/MottoPolicy";
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Set up axios defaults
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
     if (token) {
      axios.get(`${API}/users/me`)
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('authToken');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);


  const login = (token, userData) => {
    localStorage.setItem('authToken', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <div className="App">
       <Routes>
          <Route element={<Layout user={user} login={login} logout={logout} />}>
          <Route path="/" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                <HomePage />
              </Suspense>
            }
          />
          <Route path="/about" element={
              <Suspense fallback={<div>Loading...</div>}>
                <AboutUs />
              </Suspense>
            }
          />
          <Route path="/report" element={
              user ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <ReportForm />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route path="/reports" element={
              <Suspense fallback={<div>Loading...</div>}>
                <ReportsList />
              </Suspense>
            }
          />
          <Route path="/reports/:id" element={
              <Suspense fallback={<div>Loading...</div>}>
                <ReportDetail />
              </Suspense>
            }
          />
          <Route path="/dashboard" element={
              user ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <UserDashboard />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route path="/support" element={
              user ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <SupportTickets />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route path="/support/refund" element={
              user ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <SupportTickets />
                </Suspense>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/moderator" element={
              user && user.role === "moderator" ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <ModeratorPanel />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route path="/moderator/dashboard" element={
              user && user.role === "moderator" ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <ModeratorDashboard />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route path="/admin" element={
              user && (user.role === "super_admin" || user.role === "admin") ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <AdminDashboard />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route path="/motto" element={<MottoPolicy />} />
          </Route>
        </Routes>
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;